import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import DataRoomConfirmInterestModal from './components/DataRoomConfirmInterestModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import DataRoomInterestQuestionModal from './components/DataRoomInterestQuestionModal';
import DataRoomWithdraw from './components/DataRoomWithdraw';
import { SOFT_SIGNALS, SoftSignal } from 'models/Lead';
import api from 'api';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { DataRoomProgressionData } from 'services/socketService/socketEventData';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { useNavigate } from 'react-router-dom';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import { BUSINESS_ROUTE } from 'data/global';

import './DataRoomProgression.styles.scss';

type DataRoomProgressionProps = {
  className?: string;
  dataRoomId: string;
};

const DataRoomProgression: React.FC<DataRoomProgressionProps> = (props) => {
  const { className, dataRoomId } = props;

  const [data, setData] = useState<DataRoomProgressionData>();
  const [softSignal, setSoftSignal] = useState<SoftSignal>();

  const [confirmInterest, confirmInterestRef] = useCallbackRef<ModalRef>();
  const [questionModal, questionModalRef] = useCallbackRef<ModalRef>();
  const [withdrawModal, withdrawModalRef] = useCallbackRef<ModalRef>();

  const {
    shouldShowInterestPopup,
    shouldShowProgressionQuestion,
    pendingProgressionQuestion,
  } = data || {};

  const { popSuccess } = useContext(PopNotificationsContext);

  const classes = classNames('data-room-progression', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const onQuestionBack = useCallback(() => {
    questionModal?.close();
    confirmInterest?.open();
  }, [confirmInterest, questionModal]);

  const onCloseInterest = useCallback(() => {
    confirmInterest?.close();
    api.dataRoom.access.automationResponse(dataRoomId);
  }, [dataRoomId, confirmInterest]);

  const onCloseQuestion = useCallback(() => {
    questionModal?.close();
    api.dataRoom.access.automationResponse(dataRoomId, { softSignal });
  }, [dataRoomId, softSignal, questionModal]);

  const onCloseWithdraw = useCallback(() => {
    withdrawModal?.close();
    api.dataRoom.access.automationResponse(dataRoomId, {
      softSignal: SOFT_SIGNALS.DISLIKE,
    });
  }, [dataRoomId, withdrawModal]);

  const [onConfirmInterest, isLoadingConfirmInterest] = useCallbackAsync(
    async (softSignal: SoftSignal) => {
      if (softSignal === SOFT_SIGNALS.DISLIKE) {
        confirmInterest?.close();
        withdrawModal?.open();

        return;
      }

      if (shouldShowProgressionQuestion) {
        confirmInterest?.close();
        questionModal?.open();
        setSoftSignal(softSignal);

        return;
      }

      await api.dataRoom.access.automationResponse(dataRoomId, { softSignal });

      confirmInterest?.close();
    },
  );

  const [onWithdraw, isLoadingWithdraw] = useCallbackAsync(
    async (withdrawInterest) => {
      await api.dataRoom.access.automationResponse(dataRoomId, {
        softSignal: SOFT_SIGNALS.DISLIKE,
        withdrawInterest,
      });

      withdrawModal?.close();
      if (withdrawInterest) navigate(BUSINESS_ROUTE, { replace: true });
    },
  );

  const onAnswerQuestion = useCallback(
    async (answerId) => {
      await api.dataRoom.access.automationResponse(dataRoomId, {
        softSignal,
        answerId,
      });
      questionModal?.close();
      popSuccess(t('yourResponseWasSentToSellSide'));
    },
    [dataRoomId, softSignal, questionModal, t, popSuccess],
  );

  const dataRoomProgression = useCallback(
    (payload: DataRoomProgressionData) => {
      const { shouldShowInterestPopup, shouldShowProgressionQuestion } =
        payload;

      if (payload.dataRoomId !== dataRoomId) return;

      setData(payload);

      if (confirmInterest && shouldShowInterestPopup) {
        confirmInterest?.open();
        return;
      }

      if (questionModal && shouldShowProgressionQuestion) questionModal?.open();
    },
    [dataRoomId, confirmInterest, questionModal],
  );

  useSocketEvent(LISTEN_EVENTS.DATA_ROOM_PROGRESSION, dataRoomProgression);

  return (
    <div className={classes}>
      <DataRoomConfirmInterestModal
        ref={confirmInterestRef}
        isLoading={isLoadingConfirmInterest}
        onConfirmInterest={onConfirmInterest}
        onX={onCloseInterest}
        onOverlayClose={onCloseInterest}
      />
      <DataRoomInterestQuestionModal
        ref={questionModalRef}
        pendingProgressionQuestion={pendingProgressionQuestion}
        handleSubmit={onAnswerQuestion}
        onX={onCloseQuestion}
        onOverlayClose={onCloseQuestion}
        {...(shouldShowInterestPopup && { onBack: onQuestionBack })}
      />
      <DataRoomWithdraw
        ref={withdrawModalRef}
        isLoading={isLoadingWithdraw}
        onWithdraw={onWithdraw}
        onX={onCloseWithdraw}
        onOverlayClose={onCloseWithdraw}
      />
    </div>
  );
};

export default DataRoomProgression;
